<template>
  <v-dialog v-model="isOpen" max-width="800px" persistent>
    <v-card class="pa-0 rounded-lg">
      <v-card-title class="px-6 pt-6 pb-2 d-flex align-center">
        <v-icon color="#F05A24" class="mr-3">fas fa-truck</v-icon>
        <span class="text-orange-darken-2 text-h6 font-weight-bold">
          {{ $t("views.trips.create_trip.upload_excel_title").toUpperCase() }}
        </span>
        <v-spacer />
        <v-btn icon @click="$emit('close')">
          <v-icon color="#1D1B59">fas fa-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pt-4">
        <!-- HEADER DE VALIDACIÓN -->
        <v-row align="center" class="px-2 py-2">
          <v-col cols="auto" class="d-flex align-center">
            <v-avatar size="24" color="#1D1B59">
              <v-icon size="12" color="white">fas fa-check</v-icon>
            </v-avatar>
            <span class="ml-2 text-body-2 font-weight-medium" style="color: #1D1B59">
              {{ $t("views.trips.create_trip.upload_label") }}
            </span>
          </v-col>
          <v-col class="px-2">
            <div style="border-bottom: 1px solid #D9D9D9; width: 100%;"></div>
          </v-col>
          <v-col cols="auto" class="d-flex align-center">
            <v-avatar size="24" :color="validationStatus.color">
              <v-icon size="12" color="white">{{ validationStatus.icon }}</v-icon>
            </v-avatar>
            <span class="ml-2 text-body-2 font-weight-medium" :style="{ color: validationStatus.color }">
              {{ validationStatus.label }}
            </span>
          </v-col>
        </v-row>
        <!-- DRAG & DROP AREA -->
        <v-container v-if="!validationResult" fluid class="px-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div class="d-flex align-center px-4 py-12 justify-center flex-column"
                style="border: 2px dashed #D9D9D9; border-radius: 8px; background-color: #FAFAFA;"
                @click="triggerFileInput" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop" :style="{ backgroundColor: isDragging ? '#F0F0F0' : '#FAFAFA' }">
                <v-icon size="64" color="#1D1B59">fas fa-file-upload</v-icon>
                <div class="mt-4 font-weight-medium text-center">
                  {{ $t("views.trips.create_trip.drag_or_click") }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-col v-if="excelFile && !validationResult" cols="12" class="d-flex align-center px-0">
          <v-btn icon small @click="excelFile = null" class="mr-2">
            <v-icon color="#1D1B59" size="20">fas fa-times-circle</v-icon>
          </v-btn>
          <v-icon color="#1D1B59" class="mr-2">fas fa-file-excel</v-icon>
          <span class="text-body-2 font-weight-medium" style="color: #1D1B59">
            {{ excelFile.name }}
          </span>
        </v-col>

        <!-- VALIDATION SUMMARY -->
        <v-container v-if="validationResult" fluid class="px-2">
          <v-row>
            <v-col cols="12" class="py-1">
              <v-icon left size="18" color="#1D1B59">fas fa-check</v-icon>
              <strong>{{ $t("views.trips.create_trip.valid_format") }}:</strong>
              {{ validationResult.isValid ? $t("commons.yes") : $t("commons.no") }}
            </v-col>
            <v-col cols="12" class="py-1">
              <v-icon left size="18" color="#1D1B59">fas fa-file-alt</v-icon>
              <strong>{{ $t("views.trips.create_trip.total_offers") }}:</strong>
              {{ validationResult.totalOffers }}
            </v-col>
            <v-col cols="12" class="py-1">
              <v-icon left size="18" color="#1D1B59">fas fa-check</v-icon>
              <strong>{{ $t("views.trips.create_trip.valid_offers") }}:</strong>
              {{ validationResult.validOffers }}
            </v-col>
            <v-col cols="12" class="mb-4 py-1">
              <v-icon left size="18" color="#1D1B59">fas fa-times</v-icon>
              <strong>{{ $t("views.trips.create_trip.invalid_offers") }}:</strong>
              {{ validationResult.invalidOffers }}
            </v-col>
          </v-row>

          <v-card class="pa-4" outlined>
            <v-card-title class="text-h6 font-weight-bold mb-2">
              <v-icon left color="#1D1B59">fas fa-list</v-icon>
              {{ $t("views.trips.create_trip.offer_details") }}
            </v-card-title>
            <v-divider class="mb-4" />

            <!-- CONTENEDOR SCROLLABLE -->
            <div id="offer-scroll-details">
              <v-row v-for="(trip, index) in mergedTrips" :key="'trip_' + index" class="mb-4">
                <v-col cols="12">
                  <v-row no-gutters class="align-center mb-1">
                    <v-col cols="auto">
                      <v-icon :color="trip.type === 'error' ? '#1D1B59' : '#1D1B59'" class="mr-2">fas fa-truck</v-icon>
                    </v-col>
                    <v-col>
                      <strong class="text-body-1" style="color: #1D1B59">{{ trip.filename }}</strong>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="auto">
                      <v-icon small left :color="trip.type === 'error' ? '#1D1B59' : '#1D1B59'" class="mr-2">
                        {{ trip.type === 'error' ? 'fas fa-times' : 'fas fa-check' }}
                      </v-icon>
                    </v-col>
                    <v-col>
                      <span class="text-body-2 font-weight-regular" style="color: #1D1B59">
                        {{ trip.message }}
                      </span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-card>

        </v-container>

        <input ref="inputFile" type="file" accept=".xlsx,.xls" hidden @change="onFileSelected" />
      </v-card-text>
      <!-- BOTONES -->
      <v-card-actions class="px-6 pb-6 justify-space-between">
        <v-btn color="#F05A24" class="white--text py-4 px-6" @click="handleClose">
          <v-icon left>fas fa-plus-square</v-icon>
          {{ $t("views.trips.create_trip.cancel") }}
        </v-btn>

        <v-col cols="auto">
          <v-progress-circular v-if="isLoading" indeterminate color="orange darken-2" size="24" width="2"
            class="mr-3" />
        </v-col>
        <!-- Botón cambiará según si ya hay resultado -->
        <v-btn v-if="!validationResult" :disabled="!excelFile" :color="excelFile ? '#1D1B59' : '#D9D9D9'"
          class="white--text py-4 px-6" @click="uploadFile">
          <v-icon left>fas fa-truck-moving</v-icon>
          {{ $t("views.trips.create_trip.validate_trips") }}
        </v-btn>

        <v-btn v-else color="#1D1B59" class="white--text py-4 px-6" @click="createOffers">
          <v-icon left>fas fa-plus</v-icon>
          {{ $t("views.trips.create_trip.create_offers") }}
        </v-btn>
      </v-card-actions>

    </v-card>
    <notification-alert ref="notification" />
  </v-dialog>
</template>

<script>
import NotificationAlert from '@/components/views/Trip/Create/components/NotificationAlert.vue';
import { getLastTripNumber } from "@/components/views/Settings/ConfigTripNumber/Services/useTripNumberConfig";
export default {
  name: "UploadExcelModal",
  props: {
    isOpen: Boolean
  },
  components: {
    NotificationAlert,
  },
  data() {
    return {
      isLoading: false,
      excelFile: null,
      isDragging: false,
      validationStatus: {
        label: this.$t("views.trips.create_trip.waiting_validation"),
        color: "#1D1B59",
        icon: "fas fa-pen"
      },
      validationResult: null
    };
  },
  computed: {

    mergedTrips() {
      if (!this.validationResult) return [];

      return (this.validationResult.invalidTrips || []).map(item => ({
        ...item,
        type: 'error',
      }));
    }
  },
  methods: {
    handleClose() {
      this.excelFile = null;
      this.validationResult = null;
      this.validationStatus = {
        label: this.$t("views.trips.create_trip.waiting_validation"),
        color: "#1D1B59",
        icon: "fas fa-pen"
      };
      this.$emit("close");
    },
    async createOffers() {
      try {
        const formData = new FormData();
        formData.append("file", this.excelFile);
        this.isLoading = true;
        const response = await this.axios.post("/Travels/CreateMultipleTripsByExcelAsync", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutos (300,000 ms)
        });

        this.$refs.notification.show({
          message: this.$t("views.trips.create_trip.offer_creation_success"),
          type: "success",
        });
        const config = await getLastTripNumber(this.axios);
        setTimeout(() => {
          this.$emit("close");
          this.excelFile = null;
          this.validationResult = null;
          this.$emit("offers-created", config?.fullTripNumber || null);
        }, 1000);

      } catch (error) {
        this.$refs.notification.show({
          message: error?.message || this.$t("views.trips.create_trip.offer_creation_error"),
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }

    },
    triggerFileInput() {
      this.$refs.inputFile.click();
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file && this.isValidExcel(file)) {
        this.excelFile = file;
      } else {
        this.excelFile = null;
        this.$refs.notification.show({
          message: this.$t("views.trips.create_trip.invalid_file_format"),
          type: "error",
        });
      }
      this.$refs.inputFile.value = null;
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && this.isValidExcel(file)) {
        this.excelFile = file;
      } else {
        this.excelFile = null;
        this.$refs.notification.show({
          message: this.$t("views.trips.create_trip.invalid_file_format"),
          type: "error",
        });
      }
    },
    isValidExcel(file) {
      const validTypes = [
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      ];
      const validExtensions = [".xls", ".xlsx"];
      const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();

      return validTypes.includes(file.type) && validExtensions.includes(fileExtension);
    },
    async uploadFile() {
      if (!this.excelFile) return;

      const formData = new FormData();
      formData.append("file", this.excelFile);
      this.isLoading = true;
      try {
        const response = await this.axios.post("/Travels/ValidationCreateManualTripByExcelAsync", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutos (300,000 ms)
        });
        this.validationResult = response.data;
        this.$refs.notification.show({
          message: this.$t("views.trips.create_trip.upload_success"),
          type: "success",
        });

        this.validationStatus = {
          label: this.$t("views.trips.create_trip.validated_success"),
          color: "#1D1B59",
          icon: "fas fa-check",
        };

      } catch (error) {
        this.$refs.notification.show({
          message: error?.message || this.$t("views.trips.create_trip.upload_error"),
          type: "error",
        });

        this.validationStatus = {
          label: this.$t("views.trips.create_trip.validation_failed"),
          color: "#DB1919",
          icon: "fas fa-times",
        };
      } finally {
        this.isLoading = false;
      }

    },
  }
};
</script>
<style lang="scss" scoped src="../TripCreateForm.module.scss" />
